<template>
    <v-dialog :fullscreen="isMobile" v-model="dialog" transition="slide-x-reverse-transition" @click:outside="hide"
              max-width="600px">
        <v-card tile>

            <v-app-bar color="white" flat>
                <v-spacer></v-spacer>
                <div class="app-title">
                    {{ $t('exercises.select_statistics_exercises') }}
                </div>
                <v-spacer></v-spacer>
                <v-btn icon @click="hide">
                    <v-icon>mdi-close</v-icon>
                </v-btn>
            </v-app-bar>

            <v-card-text>
                <v-sheet light>
                    <v-row>
                        <v-col cols="12" class="pt-0 pb-2">
                            <v-text-field
                                ref="searchInput"
                                :placeholder="$t('common.search')"
                                append-icon="mdi-magnify"
                                :loading="loadingSearch"
                                @click:append="sendTextQuery"
                                @keyup.enter="sendTextQuery"
                                hide-details
                                outlined
                                dense
                                v-model="search"
                                color="secondary"
                                class="placeholder-text mx-auto"
                                clearable
                                @click:clear="search=''"
                            />
                        </v-col>
                        <v-col cols="6" md="6" class="pt-1 pb-0 pr-1">
                            <kimsa-combobox
                                :placeholder="$t('exercises.pick_one_category')"
                                outlined
                                dense
                                :items="categoriesOptions"
                                @change="categories = $event"
                                @clear="onClearCategories"
                                placeholder-class
                                ref="categories"
                            />
                        </v-col>
                        <v-col cols="6" md="6" class="pt-1 pb-0 pl-1">
                            <kimsa-combobox
                                :placeholder="$t('exercises.pick_type')"
                                outlined
                                dense
                                :items="typesOptions"
                                @change="types = $event"
                                @clear="onClearTypes"
                                placeholder-class
                                multiple-with-chips
                                :show-close-button="!!categories"
                                ref="types"
                            />
                        </v-col>
                    </v-row>

                    <div v-if="exercisesOptions.length === 0" class="text-center pt-4">
                        <div class="my-5"></div>
                        <small v-if="firstSearch">{{ $t('exercises.write_to_search_or_pick') }}</small>
                        <small v-else>{{ $t('common.not_found') }}</small>
                    </div>

                    <v-virtual-scroll
                        style="overflow-x: hidden;"
                        :bench="8"
                        :items="exercisesOptions"
                        :height="exercisesHeightContainer"
                        item-height="90"
                    >
                        <template v-slot:default="{ item, index }">
                            <add-exercise-card :exercise="item" :added="isExerciseAdded(item.objectID)"
                                               @add="addExercise($event)"/>
                            <v-divider class="divider"
                                       v-if="exercisesOptions.length > index+1"></v-divider>
                        </template>
                    </v-virtual-scroll>
                </v-sheet>
                <v-row>
                    <v-col cols="12" align="center" class="pb-0">
                        <v-btn color="primary" class="black--text" depressed @click="saveExercises"
                               :disabled="disabled" :loading="loadingSave">
                            {{ $t('common.save') }}
                        </v-btn>
                    </v-col>
                </v-row>
            </v-card-text>

        </v-card>
    </v-dialog>
</template>

<script>

import {mapActions, mapMutations, mapState} from 'vuex'
import {db} from "@/utils/Firebase";
import KimsaCombobox from "@/components/common/inputs/KimsaCombobox";
import AddExerciseCard from "@/components/coach/routines/AddExerciseCard";
import {ExerciseIndex} from "@/utils/Algolia";
import {debounce} from "@/utils/Common";
import _map from "lodash/map";

export default {
    name: "ExercisesSelectorStatisticsDialog",
    components: {AddExerciseCard, KimsaCombobox},
    props: {
        coachId: {
            type: String,
            default: null,
        },
    },
    data() {
        return {
            dialog: false,
            firstSearch: true,
            loadingSearch: false,
            loadingSave: false,
            showSearch: false,
            search: '',
            categories: [],
            types: [],
            exercises: [],
            exercisesOptions: [],
            type: '',
        }
    },
    computed: {
        ...mapState(['dialogActive', 'categoriesOptions']),
        typesOptions(vm = this) {
            if (vm.categories === undefined || vm.categories.length === 0) return []
            return vm.categories.types.map(type => {
                return {
                    name: type,
                    value: type,
                }
            })
        },
        exercisesHeightContainer(vm = this) {
            if (vm.exercisesOptions.length === 0) return 0
            if (vm.isMobile) return vm.$vuetify.breakpoint.height - 230
            return vm.$vuetify.breakpoint.height - 430
        },
        disabled(vm = this) {
            return vm.exercises.length === 0
        }
    },
    async mounted() {
        const vm = this
        if (vm.categoriesOptions.length === 0) {
            await vm.fetchExerciseCategories()
        }
        await vm.setDefaults()
    },
    methods: {
        ...mapMutations(['setDialogActive']),
        ...mapActions(['fetchExerciseCategories']),
        show() {
            const vm = this
            vm.dialog = true
            vm.setDialogActive(true)
        },
        hide() {
            const vm = this
            vm.dialog = false
            vm.setDialogActive(false)
            vm.reset()
            vm.$emit('hide')
        },
        reset() {
            const vm = this
            vm.search = ''
            vm.categories = []
            if(vm.dialog) {
                vm.$nextTick(() => {
                    vm.$refs.categories.model = ''
                    vm.$refs.types.model = ''
                })
            }
            vm.types = []
            vm.exercises = []
            vm.exercisesOptions = []
        },
        onTypesChange(types) {
            const vm = this
            vm.types = types
        },
        debouncedLoadExercises: debounce(
            function () {
                this.loadExercises()
            }, 500),
        sendTextQuery() {
            const vm = this
            if (!vm.search) return
            vm.loadExercises()
        },
        async loadExercises() {
            let vm = this
            vm.exercisesOptions = []
            vm.firstSearch = false
            let term = vm.search || ''
            let filter = `(coachId:"${vm.coachId}" OR owner:"app") AND hide!=1`
            vm.loadingSearch = true

            if (vm.types && vm.types.length > 0) {
                let types = vm.types.map(type => type.name)
                filter += ` AND (types:"${types.join('" OR types:"')}")`
            } else {
                // return console.log('Sin tipo seleccionado')
            }

            console.log('searching by:', term, filter)
            ExerciseIndex.search(term, {
                filters: filter,
                hitsPerPage: 10000,
            })
                .then(({hits}) => {
                    console.log('hits', hits)
                    vm.exercisesOptions = hits
                })
                .catch(err => {
                    console.log(err);
                })
                .finally(() => {
                    setTimeout(function () {
                        vm.loadingSearch = false
                    }, 600)
                });
        },
        addExercise(exercise) {
            const vm = this
            if (vm.exercises.length < 10) {
                let added = false
                vm.exercises.forEach((ex, i) => {
                    if (ex.objectID === exercise.objectID) {
                        vm.exercises.splice(i, 1)
                        added = true
                    }
                })
                if (!added) vm.exercises.push(exercise)
            } else {
                vm.toast(vm.$t('exercises.max_statistics_exercises'), 'error')
            }
        },
        isExerciseAdded(id) {
            const vm = this
            let added = false
            vm.exercises.forEach((ex, i) => {
                if (ex.objectID === id) {
                    added = true
                }
            })
            return added
        },
        async setDefaults() {
            const vm = this
            if(!vm.coachId) return
            let coachRef = await db.collection('users').doc(vm.coachId).get()
            let coach = {id: coachRef.id, ...coachRef.data()}
            if(coach.statistics_exercises) vm.exercises = coach.statistics_exercises.map(ex => {
                return {objectID: ex}
            })
        },
        onClearCategories() {
            const vm = this
            vm.types = []
            vm.debouncedLoadExercises()
        },
        onClearTypes() {
            const vm = this
            vm.types = []
        },
        async saveExercises() {
            const vm = this

            let exercises = vm.exercises.map(ex => {
                return ex.objectID
            })

            vm.loadingSave = true
            await db.collection('users').doc(vm.coachId).update({
                statistics_exercises: exercises
            })
            vm.toast(vm.$t('exercises.statistics_exercises_saved'))
            vm.loadingSave = false
            vm.hide()
        }
    },
    watch: {
        dialogActive(val) {
            if (!val) this.hide()
        },
        types(val) {
            const vm = this
            if (val && val.length > 0) {
                vm.debouncedLoadExercises()
            } else {
                vm.exercisesOptions = []
            }
        },
        search() {
            const vm = this
            vm.debouncedLoadExercises()
        },
        categories(val) {
            const vm = this
            if (vm.dialog) {
                vm.$nextTick(() => {
                    vm.$refs.types.model = ''
                })
            }
            vm.types = []
        },
        coachId() {
            this.setDefaults()
        }
    }
}

</script>

<style scoped>

</style>